/**
 * SMS Triggers Hook
 * 
 * Custom hook for managing SMS trigger rules data
 * Follows the pattern from useSmsTemplates.ts
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import SmsService from '@/api/sms';
import {
  SmsTriggerRule,
  TriggerFormData,
  TriggerTemplate,
  TriggerExecutionWithCustomer,
  TriggerListOptions,
  UseTriggersList,
  UseTriggersManagement,
  UseTriggerTemplates,
  UseTriggerHistory,
  TriggerType,
  TriggerConditionFormData,
  LogicOperator,
  TimeDelay
} from '@/types/sms-triggers';

/**
 * Hook for fetching and listing triggers
 */
export const useFetchTriggers = (user?: any): UseTriggersList => {
  const [triggers, setTriggers] = useState<SmsTriggerRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTriggers = useCallback(async () => {
    if (!user?.organisationId) {
      setTriggers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Fetching triggers for organisation:', user.organisationId);

      // Use API service to fetch triggers
      const data = await SmsService.fetchTriggers(user.organisationId);

      console.log('Successfully fetched triggers:', data?.length || 0);
      setTriggers(data || []);
    } catch (err) {
      console.error('Error fetching triggers:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load triggers';
      setError(errorMessage);
      toast({
        title: 'Failed to load triggers',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [user?.organisationId]);

  useEffect(() => {
    fetchTriggers();
  }, [fetchTriggers]);

  return {
    triggers,
    loading,
    error,
    refetch: fetchTriggers
  };
};

/**
 * Hook for managing triggers (CRUD operations)
 */
export const useTriggersManagement = (user?: any): UseTriggersManagement => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createTrigger = useCallback(async (data: TriggerFormData): Promise<SmsTriggerRule> => {
    if (!user?.organisationId || !user?.id) {
      throw new Error('Organisation ID and user ID are required');
    }

    setLoading(true);
    setError(null);

    try {
      // Use API service to create trigger
      const newTrigger = await SmsService.createTrigger(data, user.organisationId, user.id);

      console.log('Successfully created trigger:', newTrigger);
      
      toast({
        title: 'Trigger created',
        description: `Successfully created trigger "${data.rule_name}"`,
      });

      return newTrigger;
    } catch (err) {
      console.error('Error creating trigger:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create trigger';
      setError(errorMessage);
      toast({
        title: 'Failed to create trigger',
        description: errorMessage,
        variant: 'destructive'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user]);

  const updateTrigger = useCallback(async (id: string, data: Partial<TriggerFormData>): Promise<SmsTriggerRule> => {
    if (!user?.id) {
      throw new Error('User ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      // Use API service to update trigger
      const updatedTrigger = await SmsService.updateTrigger(id, data, user.id);

      console.log('Successfully updated trigger:', updatedTrigger);
      
      toast({
        title: 'Trigger updated',
        description: 'Successfully updated trigger',
      });

      return updatedTrigger;
    } catch (err) {
      console.error('Error updating trigger:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update trigger';
      setError(errorMessage);
      toast({
        title: 'Failed to update trigger',
        description: errorMessage,
        variant: 'destructive'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user]);

  const toggleTrigger = useCallback(async (id: string, enabled: boolean): Promise<SmsTriggerRule> => {
    if (!user?.id) {
      throw new Error('User ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      // Use API service to toggle trigger
      const updatedTrigger = await SmsService.toggleTrigger(id, enabled, user.id);

      toast({
        title: `Trigger ${enabled ? 'enabled' : 'disabled'}`,
        description: `Successfully ${enabled ? 'enabled' : 'disabled'} trigger`,
      });

      return updatedTrigger;
    } catch (err) {
      console.error('Error toggling trigger:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to toggle trigger';
      setError(errorMessage);
      toast({
        title: 'Failed to toggle trigger',
        description: errorMessage,
        variant: 'destructive'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user]);

  const deleteTrigger = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      // Use API service to delete trigger
      await SmsService.deleteTrigger(id);

      toast({
        title: 'Trigger deleted',
        description: 'Successfully deleted trigger',
      });
    } catch (err) {
      console.error('Error deleting trigger:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete trigger';
      setError(errorMessage);
      toast({
        title: 'Failed to delete trigger',
        description: errorMessage,
        variant: 'destructive'
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createTrigger,
    updateTrigger,
    toggleTrigger,
    deleteTrigger,
    loading,
    error
  };
};

/**
 * Hook for trigger templates
 */
export const useTriggerTemplates = (): UseTriggerTemplates => {
  const [templates, setTemplates] = useState<TriggerTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch templates from API
        const apiTemplates = await SmsService.getTriggerTemplates();
        
        // Transform API response to match TriggerTemplate interface
        const transformedTemplates: TriggerTemplate[] = apiTemplates.map(template => ({
          id: template.id,
          name: template.name,
          description: template.description,
          category: template.category,
          trigger_type: template.trigger_type,
          default_conditions: template.default_conditions,
          default_logic: template.default_logic || 'AND',
          default_time_delay: template.default_time_delay,
          default_message: template.default_message,
          personalization_fields: template.personalization_fields || [],
          default_priority: template.default_priority,
          default_max_sends_per_customer: template.default_max_sends_per_customer,
          default_cooldown_days: template.default_cooldown_days,
          use_cases: template.use_cases || []
        }));
        
        setTemplates(transformedTemplates);
      } catch (err) {
        console.error('Error fetching trigger templates:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load templates';
        setError(errorMessage);
        // Set some fallback templates in case of error
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  return {
    templates,
    loading,
    error
  };
};

/**
 * Hook for trigger execution history
 */
export const useTriggerHistory = (triggerId?: string, user?: any): UseTriggerHistory => {
  const [executions, setExecutions] = useState<TriggerExecutionWithCustomer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total_pages: 1
  });

  const loadPage = useCallback(async (page: number = 1) => {
    if (!user?.organisationId) return;

    setLoading(true);
    setError(null);

    try {
      // Use API service to fetch trigger history
      const response = await SmsService.getTriggerHistory(
        user.organisationId,
        triggerId,
        page,
        pagination.per_page
      );

      if (response.success && response.data) {
        setExecutions(response.data);
        
        // Update pagination from response meta
        if (response.meta) {
          setPagination(prev => ({
            ...prev,
            page: response.meta.page,
            total_pages: response.meta.total_pages
          }));
        }
      } else {
        throw new Error(response.error || 'Failed to fetch history');
      }

    } catch (err) {
      console.error('Error fetching trigger history:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load trigger history';
      setError(errorMessage);
      toast({
        title: 'Failed to load history',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [triggerId, user?.organisationId, pagination.per_page]);

  useEffect(() => {
    loadPage(1);
  }, [triggerId, user?.organisationId]);

  return {
    executions,
    loading,
    error,
    pagination,
    loadPage
  };
};